import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from '@/providers';
import { RequireAuth, RequireGuest } from '@/guards';
import { MainLayout, DoctorLayout, AdminLayout } from '@/layouts';
import { DeepLinkHandler } from '@/hooks/use-deep-link';
import {
  DashboardPage,
  LoginPage,
  RegisterPage,
  DoctorRegisterPage,
  DoctorLandingPage,
  NotFoundPage,
  UnauthorizedPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  MFAPage,
  SessionExpiredPage,
  ProfilePage,
  VerifyEmailPage,
  MarketplacePage,
  DoctorProfilePage,
  PatientProfilePage,
  DoctorDashboardPage,
  DoctorProfileEditPage,
  DoctorAvailabilityPage,
  MyAppointmentsPage,
  AppointmentPage,
  DoctorSchedulePage,
  MedicalRecordPage,
  MedicalRecordEditorPage,
  MedicalRecordsPage,
  MedicalTimelinePage,
  AIAssistantPage,
  ClinicalSuggestionsPage,
  SOAPScribePage,
  PatientTimelinePage,
  ClinicalAlertsPage,
  TokenUsageDashboardPage,
  WaitingRoomPage,
  VideoCallPage,
  CallEndedPage,
  ConsultationSummaryPage,
  DoctorConsultationPage,
  PatientConsultationPage,
  PaymentsPage,
  WalletPage,
  WalletStatementPage,
  WithdrawHistoryPage,
  PixKeysPage,
  BankAccountsPage,
  AdminDashboardPage,
  AdminUsersPage,
  AdminDoctorsPage,
  AdminPatientsPage,
  AdminFinancialPage,
  AdminAppointmentsPage,
  AdminWalletPage,
  AdminWithdrawalsPage,
  AdminFeatureFlagsPage,
  AdminSpecialtyManagementPage,
  AdminConfigPage,
  AdminBannersPage,
  AdminQueuePage,
  AdminApiHealthPage,
  AdminAnalyticsPage,
  AdminSettingsPage,
  ExpressaPage,
  DoctorsPage,
  PrescriptionsPage,
  CertificatesPage,
  ExamsPage,
  DependentsPage,
  FavoritesPage,
  NotificationsPage,
  HistoryPage,
  DoctorPatientsPage,
  DoctorPatientRecordsPage,
  DoctorQueuePage,
  DoctorHistoryPage,
  DoctorSettingsPage,
  DoctorCertificatesPage,
  DoctorNewCertificatePage,
  DoctorPrescriptionsPage,
  DoctorNewPrescriptionPage,
  DoctorExamsPage,
  DoctorNewExamPage,
  DoctorMemedSettingsPage,
} from '@/pages';
import {
  AdminPartnersPage,
  AdminPartnerDetailPage,
  AdminCommissionsPage,
  AdminPayoutsPage,
  AdminCampaignsPage,
  AdminFraudPage,
} from '@/admin/partners';
import { LandingPageV3 } from '@/landing/v3';
import { PrivacyPage, TermsPage, HelpPage, PartnerRedirectPage, PublicValidatePrescriptionPage, HospitalGatewayPage } from '@/pages';
import { PatientSupportTicketsPage } from '@/pages/PatientSupportTicketsPage';
import { AdminSupportTicketsPage } from '@/pages/AdminSupportTicketsPage';
import {
  PartnerLayout,
  PartnerDashboardPage,
  CommissionsPage,
  PayoutsPage,
  LinksPage,
  PartnerProfilePage,
} from '@/partner/pages';

export function AppRouter() {
  return (
    <BrowserRouter>
      <DeepLinkHandler />
      <AppProvider>
        <Routes>
          <Route path="/" element={<LandingPageV3 />} />
          <Route path="/para-medicos" element={<DoctorLandingPage />} />
          <Route path="/privacidade" element={<PrivacyPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/termos" element={<TermsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/ajuda" element={<HelpPage />} />
          <Route
            path="/login"
            element={
              <RequireGuest>
                <LoginPage />
              </RequireGuest>
            }
          />
          <Route
            path="/register"
            element={
              <RequireGuest>
                <RegisterPage />
              </RequireGuest>
            }
          />
          <Route
            path="/cadastro"
            element={
              <RequireGuest>
                <RegisterPage />
              </RequireGuest>
            }
          />
          <Route
            path="/register/doctor"
            element={
              <RequireGuest>
                <DoctorRegisterPage />
              </RequireGuest>
            }
          />
          <Route
            path="/medico/cadastro"
            element={
              <RequireGuest>
                <DoctorRegisterPage />
              </RequireGuest>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RequireGuest>
                <ForgotPasswordPage />
              </RequireGuest>
            }
          />
          <Route
            path="/reset-password"
            element={
              <RequireGuest>
                <ResetPasswordPage />
              </RequireGuest>
            }
          />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/mfa" element={<MFAPage />} />
          <Route path="/session-expired" element={<SessionExpiredPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/buscar" element={<MarketplacePage />} />
          <Route path="/medicos" element={<DoctorsPage />} />
          <Route path="/medicos/:slug" element={<DoctorProfilePage />} />
          <Route path="/p/:hash" element={<PartnerRedirectPage />} />
          <Route path="/validar-receita" element={<PublicValidatePrescriptionPage />} />
          <Route path="/hospital-gateway" element={<HospitalGatewayPage />} />
          <Route
            path="/medico"
            element={
              <RequireAuth allowedRoles={['DOCTOR']}>
                <DoctorLayout />
              </RequireAuth>
            }
          >
            <Route path="dashboard" element={<DoctorDashboardPage />} />
            <Route path="perfil" element={<DoctorProfileEditPage />} />
            <Route path="fila" element={<DoctorQueuePage />} />
            <Route path="pacientes" element={<DoctorPatientsPage />} />
            <Route path="pacientes/:patientId/prontuarios" element={<DoctorPatientRecordsPage />} />
            <Route path="agenda" element={<DoctorSchedulePage />} />
            <Route path="disponibilidade" element={<DoctorAvailabilityPage />} />
            <Route path="receitas" element={<DoctorPrescriptionsPage />} />
            <Route path="receitas/novo" element={<DoctorNewPrescriptionPage />} />
            <Route path="atestados" element={<DoctorCertificatesPage />} />
            <Route path="atestados/novo" element={<DoctorNewCertificatePage />} />
            <Route path="exames" element={<DoctorExamsPage />} />
            <Route path="exames/novo" element={<DoctorNewExamPage />} />
            <Route path="historico" element={<DoctorHistoryPage />} />
            <Route path="prontuarios" element={<MedicalRecordsPage />} />
            <Route path="prontuarios/:id" element={<MedicalRecordPage />} />
            <Route path="prontuarios/:id/editar" element={<MedicalRecordEditorPage />} />
            <Route path="ia" element={<ClinicalSuggestionsPage />} />
            <Route path="ia/resumo/:id" element={<AIAssistantPage />} />
            <Route path="ia/scribe" element={<SOAPScribePage />} />
            <Route path="ia/timeline" element={<PatientTimelinePage />} />
            <Route path="ia/alertas" element={<ClinicalAlertsPage />} />
            <Route path="ia/custos" element={<TokenUsageDashboardPage />} />
            <Route path="telemedicine/session/:sessionId" element={<DoctorConsultationPage />} />
            <Route path="telemedicine/video/:sessionId" element={<VideoCallPage />} />
            <Route path="telemedicine/call-ended/:sessionId" element={<CallEndedPage />} />
            <Route path="telemedicine/summary/:sessionId" element={<ConsultationSummaryPage />} />
            <Route path="pagamentos" element={<PaymentsPage />} />
            <Route path="carteira" element={<WalletPage />} />
            <Route path="carteira/extrato" element={<WalletStatementPage />} />
            <Route path="carteira/saques" element={<WithdrawHistoryPage />} />
            <Route path="carteira/chaves-pix" element={<PixKeysPage />} />
            <Route path="carteira/contas-bancarias" element={<BankAccountsPage />} />
            <Route path="configuracoes" element={<DoctorSettingsPage />} />
            <Route path="configuracoes/memed" element={<DoctorMemedSettingsPage />} />
            <Route path="notificacoes" element={<NotificationsPage />} />
          </Route>
          <Route
            path="/"
            element={
              <RequireAuth allowedRoles={['PATIENT', 'SUPPORT']}>
                <MainLayout />
              </RequireAuth>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="expressa" element={<ExpressaPage />} />
            <Route path="historico" element={<HistoryPage />} />
            <Route path="receitas" element={<PrescriptionsPage />} />
            <Route path="atestados" element={<CertificatesPage />} />
            <Route path="exames" element={<ExamsPage />} />
            <Route path="dependentes" element={<DependentsPage />} />
            <Route path="favoritos" element={<FavoritesPage />} />
            <Route path="notificacoes" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="perfil" element={<PatientProfilePage />} />
            <Route path="medical-records" element={<MedicalTimelinePage />} />
            <Route path="medical-records/:id" element={<MedicalRecordPage />} />
            <Route path="medical-records/:id/edit" element={<MedicalRecordEditorPage />} />
            <Route path="medical-records/patient/:patientId" element={<MedicalTimelinePage />} />
            <Route path="agendamentos" element={<MyAppointmentsPage />} />
            <Route path="agendamentos/:id" element={<AppointmentPage />} />
            <Route path="expressa/payment-success" element={<ExpressaPage />} />
            <Route path="pagamentos" element={<PaymentsPage />} />
            <Route path="carteira" element={<WalletPage />} />
            <Route path="carteira/extrato" element={<WalletStatementPage />} />
            <Route path="carteira/saques" element={<WithdrawHistoryPage />} />
            <Route path="carteira/chaves-pix" element={<PixKeysPage />} />
            <Route path="carteira/contas-bancarias" element={<BankAccountsPage />} />
            <Route path="chamados" element={<PatientSupportTicketsPage />} />
          </Route>
          <Route
            path="/admin"
            element={
              <RequireAuth allowedRoles={['ADMIN']}>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="medicos" element={<AdminDoctorsPage />} />
            <Route path="pacientes" element={<AdminPatientsPage />} />
            <Route path="consultas" element={<AdminAppointmentsPage />} />
            <Route path="financeiro" element={<AdminFinancialPage />} />
            <Route path="wallet" element={<AdminWalletPage />} />
            <Route path="saques" element={<AdminWithdrawalsPage />} />
            <Route path="feature-flags" element={<AdminFeatureFlagsPage />} />
            <Route path="especialidades" element={<AdminSpecialtyManagementPage />} />
            <Route path="anuncios" element={<AdminBannersPage />} />
            <Route path="chamados" element={<AdminSupportTicketsPage />} />
            <Route path="configuracoes" element={<AdminConfigPage />} />
            <Route path="filas" element={<AdminQueuePage />} />
            <Route path="api-health" element={<AdminApiHealthPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="partners" element={<AdminPartnersPage />} />
            <Route path="partners/:id" element={<AdminPartnerDetailPage />} />
            <Route path="partners/commissions" element={<AdminCommissionsPage />} />
            <Route path="partners/payouts" element={<AdminPayoutsPage />} />
            <Route path="partners/campaigns" element={<AdminCampaignsPage />} />
            <Route path="partners/fraud" element={<AdminFraudPage />} />
          </Route>
          <Route
            path="/telemedicine"
            element={
              <RequireAuth allowedRoles={['PATIENT', 'DOCTOR', 'SUPPORT', 'ADMIN']}>
                <MainLayout />
              </RequireAuth>
            }
          >
            <Route path="waiting/:appointmentId" element={<WaitingRoomPage />} />
            <Route path="session/:sessionId" element={<DoctorConsultationPage />} />
            <Route path="consultation/:sessionId" element={<PatientConsultationPage />} />
            <Route path="video/:sessionId" element={<VideoCallPage />} />
            <Route path="call-ended/:sessionId" element={<CallEndedPage />} />
            <Route path="summary/:sessionId" element={<ConsultationSummaryPage />} />
          </Route>
          <Route
            path="/parceiro"
            element={
              <RequireAuth allowedRoles={['PARTNER', 'PATIENT', 'DOCTOR', 'SUPPORT', 'ADMIN']}>
                <PartnerLayout />
              </RequireAuth>
            }
          >
            <Route index element={<PartnerDashboardPage />} />
            <Route path="comissoes" element={<CommissionsPage />} />
            <Route path="saques" element={<PayoutsPage />} />
            <Route path="links" element={<LinksPage />} />
            <Route path="perfil" element={<PartnerProfilePage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppProvider>
    </BrowserRouter>
  );
}
